import type { BrightcovePlayer, BrightcoveTextTrack } from "@brightcove/player-loader"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { playerLog } from "../lib/playerLog"
import { clampToDuration } from "../lib/timecode"
import { DEFAULT_PLAYBACK_RATE, type PlaybackRate, type PlayerCommands, type PlayerState } from "../types"

/** Only English captions are expected, matching the TV player's `toggleCaptions`. */
const CAPTION_LANGUAGE = "en"

const CAPTION_KINDS = new Set(["captions", "subtitles"])

const EVENTS = [
  "play",
  "playing",
  "pause",
  "ended",
  "seeked",
  "seeking",
  "waiting",
  "canplay",
  "timeupdate",
  "durationchange",
  "loadedmetadata",
  "progress",
  "ratechange",
  "error",
] as const

const captionTracks = (player: BrightcovePlayer): readonly BrightcoveTextTrack[] => {
  const list = player.textTracks()
  const tracks: BrightcoveTextTrack[] = []

  for (let index = 0; index < list.length; index += 1) {
    const track = list[index]

    if (track && CAPTION_KINDS.has(track.kind)) {
      tracks.push(track)
    }
  }

  return tracks
}

const bufferedEnd = (player: BrightcovePlayer): number => {
  const ranges = player.buffered()
  const current = player.currentTime()
  let furthest = 0

  // The buffered ranges are not ordered around the playhead, and only the range
  // the playhead sits in describes what will actually play next.
  for (let index = 0; index < ranges.length; index += 1) {
    const start = ranges.start(index)
    const end = ranges.end(index)

    if (start <= current && end > furthest) {
      furthest = end
    }
  }

  return furthest
}

const isRate = (value: number): value is PlaybackRate =>
  value === 0.5 || value === 0.75 || value === 1 || value === 1.25 || value === 1.5 || value === 2

interface EngineOptions {
  /** The graph's duration, used until the player reports its own. */
  readonly fallbackDurationSeconds: number | undefined
  /** Where to resume. Applied once, on the first `loadedmetadata` for a video. */
  readonly startSeconds: number
  /** Identifies the loaded video, so switching sessions re-arms the resume seek. */
  readonly videoCloudId: string
}

export interface PlayerEngine {
  readonly state: PlayerState
  readonly commands: PlayerCommands
  /** Hand the Video.js player here once the loader has built it. */
  readonly attach: (player: BrightcovePlayer) => void
  readonly fail: () => void
  /** Re-arms the player after a failure; the caller also remounts the embed. */
  readonly reset: () => void
}

/**
 * Mirrors the imperative Video.js player into React state and exposes the only
 * commands the chrome needs.
 *
 * Everything below the seam is imperative and event-driven: the player is the
 * source of truth for time, duration, rate and captions, and this hook never
 * writes those to state speculatively — a `seekTo` moves the playhead and waits
 * for `timeupdate` to say so. That is why scrubbing needs its own local state in
 * the scrub bar rather than optimistically updating here.
 */
export const usePlayerEngine = ({
  fallbackDurationSeconds,
  startSeconds,
  videoCloudId,
}: EngineOptions): PlayerEngine => {
  const [player, setPlayer] = useState<BrightcovePlayer | null>(null)
  const [hasFailed, setHasFailed] = useState(false)
  const [status, setStatus] = useState<PlayerState["status"]>("loading")
  const [currentSeconds, setCurrentSeconds] = useState(startSeconds)
  const [playerDurationSeconds, setPlayerDurationSeconds] = useState(0)
  const [bufferedSeconds, setBufferedSeconds] = useState(0)
  const [rate, setRateState] = useState<PlaybackRate>(DEFAULT_PLAYBACK_RATE)
  const [captions, setCaptions] = useState({ isAvailable: false, isEnabled: false })

  /**
   * The resume seek must happen exactly once per video. Re-applying it on a later
   * `loadedmetadata` — which fires again on some quality switches — would yank the
   * viewer back to where they started.
   */
  const resumedVideoIdRef = useRef<string | null>(null)
  const startSecondsRef = useRef(startSeconds)
  startSecondsRef.current = startSeconds

  /**
   * Rate is a viewer preference, not a property of the video, but loading a new
   * source resets the media element to 1x. Held here so it can be re-applied on
   * every load without the chrome having to remember to.
   */
  const desiredRateRef = useRef<PlaybackRate>(DEFAULT_PLAYBACK_RATE)

  /** TEMPORARY, with `playerLog` — the last status actually printed. */
  const loggedStatusRef = useRef<PlayerState["status"]>("loading")

  const attach = useCallback((next: BrightcovePlayer) => {
    playerLog("engine attach", {
      hasPlay: typeof next?.play === "function",
      hasIsDisposed: typeof next?.isDisposed === "function",
    })
    setHasFailed(false)
    setPlayer(next)
  }, [])

  const fail = useCallback(() => {
    playerLog("engine fail")
    setHasFailed(true)
    setStatus("failed")
  }, [])

  const reset = useCallback(() => {
    setHasFailed(false)
    setStatus("loading")
    setPlayer(null)
    resumedVideoIdRef.current = null
  }, [])

  useEffect(() => {
    if (!player) {
      playerLog("engine effect: no player yet", { videoCloudId })
      return
    }

    playerLog("engine effect: wiring player", {
      videoCloudId,
      disposed: player.isDisposed?.(),
    })

    /**
     * Logs transitions rather than every sync. The comparison is held in a ref
     * rather than read from the state updater, because React double-invokes
     * updaters in development and a logging side effect inside one would print
     * every transition twice.
     */
    const applyStatus = (next: PlayerState["status"], detail: Record<string, unknown>) => {
      if (loggedStatusRef.current !== next) {
        playerLog(`status ${loggedStatusRef.current} -> ${next}`, detail)
        loggedStatusRef.current = next
      }

      setStatus(next)
    }

    const syncCaptions = () => {
      const tracks = captionTracks(player)

      setCaptions({
        isAvailable: tracks.length > 0,
        isEnabled: tracks.some(track => track.mode === "showing"),
      })
    }

    const sync = () => {
      if (player.isDisposed()) {
        playerLog("sync skipped: player disposed")
        return
      }

      const duration = player.duration()

      setCurrentSeconds(player.currentTime())
      setPlayerDurationSeconds(Number.isFinite(duration) ? duration : 0)
      setBufferedSeconds(bufferedEnd(player))

      const playerRate = player.playbackRate()
      if (isRate(playerRate)) {
        setRateState(playerRate)
      }

      applyStatus(player.ended() ? "ended" : player.paused() ? "paused" : "playing", {
        currentTime: player.currentTime(),
        duration,
        readyState: player.readyState?.(),
        networkState: player.networkState?.(),
      })
    }

    const handleLoadedMetadata = () => {
      playerLog("loadedmetadata", { duration: player.duration() })
      player.playbackRate(desiredRateRef.current)

      if (resumedVideoIdRef.current !== videoCloudId) {
        resumedVideoIdRef.current = videoCloudId

        const target = clampToDuration(startSecondsRef.current, player.duration())
        if (target > 0) {
          player.currentTime(target)
        }
      }

      syncCaptions()
      sync()
    }

    const handleError = () => {
      playerLog("player error event", { error: player.error?.() })
      setStatus("failed")
    }

    /**
     * TEMPORARY, with `playerLog`. `timeupdate` fires several times a second, so
     * each event is logged only the first time — enough to prove the pipe is
     * open without flooding the console. Held as pairs so cleanup can unbind
     * them; a fresh closure per event would leak a listener on every re-subscribe.
     */
    const seen = new Set<string>()
    const traced: readonly (readonly [string, () => void])[] = EVENTS.map(event => [
      event,
      () => {
        if (!seen.has(event)) {
          seen.add(event)
          playerLog(`first "${event}"`)
        }
      },
    ])

    for (const event of EVENTS) {
      player.on(event, sync)
    }
    for (const [event, handler] of traced) {
      player.on(event, handler)
    }
    player.on("loadedmetadata", handleLoadedMetadata)
    player.on("error", handleError)

    const trackList = player.textTracks()
    trackList.addEventListener("addtrack", syncCaptions)
    trackList.addEventListener("change", syncCaptions)

    playerLog("engine subscribed", {
      videoCloudId,
      startSeconds: startSecondsRef.current,
      currentSrc: player.currentSrc?.(),
      readyState: player.readyState?.(),
    })

    syncCaptions()
    sync()

    return () => {
      if (player.isDisposed()) {
        return
      }

      for (const event of EVENTS) {
        player.off(event, sync)
      }
      for (const [event, handler] of traced) {
        player.off(event, handler)
      }
      player.off("loadedmetadata", handleLoadedMetadata)
      player.off("error", handleError)
      trackList.removeEventListener("addtrack", syncCaptions)
      trackList.removeEventListener("change", syncCaptions)
    }
  }, [player, videoCloudId])

  const durationSeconds = playerDurationSeconds > 0 ? playerDurationSeconds : (fallbackDurationSeconds ?? 0)

  const commands = useMemo<PlayerCommands>(() => {
    const withPlayer = (run: (target: BrightcovePlayer) => void) => () => {
      if (player && !player.isDisposed()) {
        run(player)
      }
    }

    const seekTo = (seconds: number) => {
      if (!player || player.isDisposed()) {
        return
      }

      const target = clampToDuration(seconds, player.duration())
      player.currentTime(target)
      // `timeupdate` can lag a seek by a frame; the chrome should not snap back.
      setCurrentSeconds(target)
    }

    /**
     * `play()` rejects when autoplay is blocked, which is expected rather than
     * exceptional — the viewer just presses play. The rejection is logged
     * because a swallowed one is indistinguishable from a player that simply
     * never starts.
     */
    const start = (target: BrightcovePlayer) => {
      const result = target.play()

      if (result) {
        result.then(() => playerLog("play() resolved")).catch((error: unknown) => playerLog("play() rejected", error))
      } else {
        playerLog("play() returned no promise")
      }
    }

    return {
      play: withPlayer(start),
      pause: withPlayer(target => target.pause()),
      togglePlay: withPlayer(target => {
        if (target.paused()) {
          start(target)
        } else {
          target.pause()
        }
      }),
      seekTo,
      skip: (deltaSeconds: number) => {
        if (player && !player.isDisposed()) {
          seekTo(player.currentTime() + deltaSeconds)
        }
      },
      setRate: (next: PlaybackRate) => {
        desiredRateRef.current = next
        setRateState(next)
        if (player && !player.isDisposed()) {
          player.playbackRate(next)
        }
      },
      toggleCaptions: withPlayer(target => {
        const tracks = captionTracks(target)
        const shouldEnable = !tracks.some(track => track.mode === "showing")
        const preferred = tracks.find(track => track.language === CAPTION_LANGUAGE) ?? tracks[0]

        for (const track of tracks) {
          track.mode = shouldEnable && track === preferred ? "showing" : "disabled"
        }

        setCaptions({ isAvailable: tracks.length > 0, isEnabled: shouldEnable && Boolean(preferred) })
      }),
    }
  }, [player])

  const state = useMemo<PlayerState>(
    () => ({
      status: hasFailed ? "failed" : status,
      currentSeconds,
      durationSeconds,
      bufferedSeconds,
      rate,
      captions,
    }),
    [hasFailed, status, currentSeconds, durationSeconds, bufferedSeconds, rate, captions],
  )

  return { state, commands, attach, fail, reset }
}
